import { useEffect } from 'react'
import { useLocation, useNavigate, type NavigateFunction } from 'react-router-dom'
import { currentSub, initializeSession, sessionRefused, signOut } from './auth'
import { drainQueue, getPending, releaseAllHeld } from './idb-queue'
import { clearAllDrafts } from './route-draft'

// R493 (Dee via Rook #66417; scope and trigger by Frank #66467): sign-out and a revoked session
// clear HOP's own shell and land on the login page; a reload after it must not show a cached
// cockpit.
//
// SCOPE (Frank #66467): HOP's OWN cache and HOP's OWN worker, nothing else on the origin.
// - Cache Storage: only HOP_CACHE_NAME (must equal CACHE_NAME in public/sw.js; a cell pins
//   the two together). Any other app's cache on this origin is left alone.
// - Service worker: only the registration whose scope is HOP's base (the scope main.tsx
//   registers `${BASE_URL}sw.js` under). Any other registration on the origin is left alone. The next document load
//   registers HOP's again (main.tsx), so the offline shell comes back signed out.
// - The session cookie is httpOnly: page code cannot touch it. The gateway clears it on
//   POST /auth/logout; this file only calls that route through lib/auth.ts's signOut().
// - Drafts (route-draft.ts) are cleared, so the next person on the device sees none.
// An app on another host (another origin) is out of reach of any page or worker here.
//
// THE QUEUE (lib/idb-queue.ts, IndexedDB 'tkf-hop' / 'action-queue') is NEVER deleted here.
// Each queued write carries its person's id and is sent only under that person's session, so
// after a sign-out it waits on this device until they sign in again (or ages out at 24 h).

export const HOP_CACHE_NAME = 'tkf-hop-v1'

function hopScope(): string {
  return new URL(import.meta.env.BASE_URL, window.location.origin).href
}

// One registration per scope on an origin, so HOP's scope names HOP's registration alone.
function isHopRegistration(reg: ServiceWorkerRegistration): boolean {
  return reg.scope === hopScope()
}

/** Deletes HOP's cache and unregisters HOP's worker; nothing else on the origin. */
export async function clearAppShell(): Promise<void> {
  clearAllDrafts()
  try {
    if (typeof caches !== 'undefined') await caches.delete(HOP_CACHE_NAME)
  } catch {
    // A blocked or missing Cache Storage holds nothing to clear.
  }
  try {
    if (typeof navigator !== 'undefined' && navigator.serviceWorker && typeof navigator.serviceWorker.getRegistrations === 'function') {
      const regs = await navigator.serviceWorker.getRegistrations()
      await Promise.all(regs.filter(isHopRegistration).map((r) => r.unregister()))
    }
  } catch {
    // Same: nothing registered, nothing to unregister.
  }
}

// A full document load of the login page: no screen, session cache or record of the signed-in
// person survives in memory, and the worker is registered afresh. Replaceable in tests only
// (jsdom does not navigate documents).
let leave: (url: string) => void = (url) => window.location.replace(url)

export function _setLeaveForTest(fn: ((url: string) => void) | null): void {
  leave = fn ?? ((url) => window.location.replace(url))
}

const LOGIN_URL = `${import.meta.env.BASE_URL}login`

/** After a sign-out the gateway accepted: clear the shell, then land on the login page. */
export async function landSignedOut(navigate: NavigateFunction): Promise<void> {
  await clearAppShell()
  navigate('/login', { replace: true })
  leave(LOGIN_URL)
}

/**
 * MANUAL sign-out, step one (Frank #66467): the session is still alive, so this person's queued
 * writes are SENT first, through the queue's own drain (same rules as a reconnect). Returns how
 * many of this person's writes are still waiting afterwards (offline, a 5xx retry, a paused
 * queue); the caller asks before signing out over them. A queue that cannot be read counts 0
 * and never blocks a sign-out.
 */
export async function sendQueuedThenCount(): Promise<number> {
  const me = currentSub()
  // R517: a move still inside its Undo window is released FIRST, so the drain below sends it
  // and the count below counts it. Only this manual path; the automatic 401 clear leaves it held.
  try {
    await releaseAllHeld(me)
  } catch {
    /* a queue that cannot be read holds nothing to release */
  }
  try {
    await drainQueue()
  } catch {
    /* the count below says what is still waiting */
  }
  try {
    return (await getPending()).filter((item) => item.sub === me).length
  } catch {
    return 0
  }
}

/**
 * Rendered once inside the router (App.tsx); renders nothing. The bootstrap session check
 * (initializeSession, the one memoized fetch) answering 401 on a screen that needs a session
 * means the gateway refused the cookie: revoked, expired or signed out elsewhere. Then: the
 * gateway's logout clears the cookies, HOP's shell is cleared, the page lands on login. The
 * queue is kept. ONLY a 401 (Frank #66467): a 403, a 5xx, a 429, a timeout or no network is
 * "could not check", and the shell stays. Not on /login itself (that is where a signed-out
 * person belongs; clearing there would loop).
 */
export function SessionLossWatcher() {
  const location = useLocation()
  const navigate = useNavigate()
  useEffect(() => {
    if (location.pathname === '/login') return
    let live = true
    void initializeSession().then(async (session) => {
      if (!live || !sessionRefused(session)) return
      await signOut()
      await landSignedOut(navigate)
    })
    return () => {
      live = false
    }
    // Once per document load: the bootstrap check. A session that ends later answers 401 on its
    // next call and opens the re-auth overlay (App.tsx), which keeps the person's queued work.
  }, [])
  return null
}
